import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BiContextProvidedValue,
  BiData,
  CommentButtonClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { EditorContext } from '@wix/da-editor-tiptap/pkg/components/Editor/Context';
import { getCompliantLocalStorageItem } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/selectors';
import localStorageWrapper from '@wix/da-react-context/pkg/compliantLocalStorage/localStorageWrapper';
import { CompliantLocalStorageKeys } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/constants';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { useEditorDraftActions } from '@wix/da-editor-tiptap/pkg/clientDrafts/hooks';
import { publishComment } from '../redux/actionCreators';
import { useVerification } from '../../VerificationPopup/withVerification';
import { CommentEditorContext } from '../context';
import { useIsCommentEditorSubmittable } from './useIsCommentEditorSubmittable';
import { CommentEditorSource } from '../redux/types';

/**
 * Provides a function that will post a comment!
 *
 * Relies on all the contexts provided by `<CommentEditorProviders />`,
 * so be sure you have it set up first!
 */
export function useCommentPostAction({
  messageId,
  folderId,
  shouldDeleteMessage,
  onPost,
}: {
  messageId?: string;
  folderId?: number | null;
  shouldDeleteMessage?: boolean;
  onPost?: () => void;
} = {}) {
  const {
    isBusy,
    editorId,
    commentableType,
    commentableItemId,
    editorSource,
    relevantComment,
  } = useContext(CommentEditorContext);
  const {
    ref: editor,
    isReady: isEditorReady,
    getStateForSave: getEditorStateForSave,
  } = useContext(EditorContext);
  const dispatch = useDispatch();
  const { pushVerificationPopup, isVerificationNeeded } = useVerification();
  const { removeEditorDraft } = useEditorDraftActions(editorId);
  const isSubmittable = useIsCommentEditorSubmittable();
  const logBiEvent = useBiLogger();
  const exclusive_avatars = useFlag('exclusive_avatars');

  const lastExclusiveRawFromSelector = useSelector(state =>
    getCompliantLocalStorageItem(
      state as any,
      CompliantLocalStorageKeys.EXCLUSIVE_AVATAR_LAST_SELECTED
    )
  );

  const postComment = useCallback(async () => {
    if (isBusy || !isSubmittable || !isEditorReady) {
      return;
    }

    if (isVerificationNeeded) {
      pushVerificationPopup('comment');
      return;
    }

    // Clean up draft state in local storage
    removeEditorDraft();

    // Compute whether the editor currently contains an exclusive avatar node BEFORE any automatic insertion
    let hasExclusiveAvatar = false;

    // If not editing: enforce last-selected exclusive avatar from storage into the document
    // Ensures exactly one da-exclusive-avatar node with that selection (clean old nodes first, then insert)
    if (exclusive_avatars && editorSource !== CommentEditorSource.Edit) {
      try {
        // Read directly from localStorage at submit time to avoid any potential saga lag
        const raw =
          localStorageWrapper.getItem(
            CompliantLocalStorageKeys.EXCLUSIVE_AVATAR_LAST_SELECTED as any
          ) ?? lastExclusiveRawFromSelector;
        const parsed = raw ? JSON.parse(raw) : null;

        if (parsed && (parsed as any).deviationId && editor && isEditorReady) {
          hasExclusiveAvatar = true;

          const { state, view } = editor as any;
          const tr = state.tr;
          const ranges: Array<{ from: number; to: number }> = [];

          state.doc.descendants((node: any, pos: number) => {
            if (node?.type?.name === 'da-exclusive-avatar') {
              ranges.push({ from: pos, to: pos + node.nodeSize });
            }
          });

          ranges
            .sort((a, b) => b.from - a.from)
            .forEach(({ from, to }) => tr.delete(from, to));

          if (tr.docChanged) {
            view.dispatch(tr);
          }

          try {
            // Move the selection to a safe text position (end of document)
            // so we don't replace a selected block
            const endPos = Math.max(1, state.doc.content.size);
            editor
              .chain()
              .setTextSelection(endPos)
              .insertContent({
                type: 'da-exclusive-avatar',
                attrs: { deviation: parsed },
              } as any)
              .run();
          } catch (e) {}
        }
      } catch (e) {}
    }

    if (editorSource !== CommentEditorSource.Edit) {
      logBiEvent(
        BiData<CommentButtonClickBiEvent>({
          evid: 184,
          itemid:
            typeof commentableItemId === 'string'
              ? parseInt(commentableItemId)
              : commentableItemId,
          typeid: commentableType,
          notification_type: BiContextProvidedValue,
          is_comment_thread: BiContextProvidedValue,
          is_reply: editorSource === CommentEditorSource.Reply ? 1 : 0,
          is_sticky: 0,
          has_exclusive: hasExclusiveAvatar ? 1 : 0,
        })
      );
    }
    dispatch(
      publishComment({
        editorId,
        typeId: commentableType,
        itemId: commentableItemId,
        rawContentState: JSON.stringify(getEditorStateForSave()),
        editComment:
          editorSource === CommentEditorSource.Edit
            ? relevantComment
            : undefined,
        replyComment:
          editorSource === CommentEditorSource.Reply
            ? relevantComment
            : undefined,
        messageId,
        folderId,
        shouldDeleteMessage,
      })
    );

    onPost && onPost();
  }, [
    dispatch,
    isSubmittable,
    isEditorReady,
    getEditorStateForSave,
    isBusy,
    editor,
    editorId,
    editorSource,
    relevantComment,
    commentableType,
    commentableItemId,
    pushVerificationPopup,
    isVerificationNeeded,
    removeEditorDraft,
    logBiEvent,
    messageId,
    shouldDeleteMessage,
    folderId,
    onPost,
    lastExclusiveRawFromSelector,
    exclusive_avatars,
  ]);
  return postComment;
}
